/**
 * Маршруты приложения.
 *
 * Кнопка «назад» у Telegram своя, поэтому внутри разделов навигации нет:
 * два механизма возврата на одном экране путают.
 */
import { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';

import { GuestNotice } from '@/features/guest/GuestNotice';
import { HomePage } from '@/pages/home/HomePage';
import { PlansPage } from '@/pages/plans/PlansPage';
import { ProfilePage } from '@/pages/profile/ProfilePage';
import { ReferralPage } from '@/pages/referral/ReferralPage';
import { SubscriptionPage } from '@/pages/subscriptions/SubscriptionPage';
import { SubscriptionsPage } from '@/pages/subscriptions/SubscriptionsPage';
import { isGuest } from '@/shared/lib/guest';
import { Header } from '@/shared/ui/kit/Header';
import { TabBar, type Tab } from '@/shared/ui/kit/TabBar';

/**
 * Вкладка, к которой относится экран.
 *
 * Вложенные экраны подсвечивают свой раздел: иначе на «Моих подписках»
 * не выделена ни одна вкладка и непонятно, где находишься.
 */
function tabFor(pathname: string): string {
  if (pathname.startsWith('/subscriptions')) return '/';
  if (pathname.startsWith('/referral')) return '/profile';
  return pathname;
}

const TABS: readonly Tab[] = [
  { path: '/', label: 'Главная', icon: 'home' },
  { path: '/plans', label: 'Тарифы', icon: 'tag' },
  { path: '/profile', label: 'Профиль', icon: 'user' },
];

// Экраны аккаунта в режиме просмотра: данных нет, есть приглашение в бота.
const GuestProfile = () => (
  <GuestNotice
    icon="user"
    title="Профиль — в Telegram"
    text="Подписки, устройства и история оплат привязаны к аккаунту Telegram. Откройте бота — вход без регистрации и паролей."
  />
);

const GuestReferral = () => (
  <GuestNotice
    icon="users"
    title="Приглашайте друзей"
    text="Личная реферальная ссылка и начисления — в Telegram-боте. Откройте его, чтобы получить свою ссылку."
  />
);

export function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Кнопка под сообщением бота об оплате открывает приложение с ?sub=<id>:
    // человек попадает сразу на оплаченную подписку, а не на главную.
    const params = new URLSearchParams(location.search);
    const sub = params.get('sub');
    if (sub && /^[0-9a-f-]{36}$/i.test(sub)) {
      void navigate(`/subscriptions/${sub}`, { replace: true });
    }
    // Только при запуске: дальше адрес меняет сама навигация.
  }, []);

  useEffect(() => {
    // Прокрутка вверх при смене экрана: иначе новый экран открывается
    // на том же месте, где человек остановился на предыдущем.
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <Header />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/profile" element={isGuest() ? <GuestProfile /> : <ProfilePage />} />
        <Route path="/referral" element={isGuest() ? <GuestReferral /> : <ReferralPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/subscriptions/:id" element={<SubscriptionPage />} />
        <Route path="*" element={<HomePage />} />
      </Routes>

      <TabBar
        tabs={TABS}
        current={tabFor(location.pathname)}
        onSelect={(path) => void navigate(path)}
      />
    </>
  );
}
